import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StocktakingProvider } from "@/context/StocktakingContext";
import Dashboard from "@/pages/Dashboard";
import ImportPage from "@/pages/ImportPage";
import PickingPage from "@/pages/PickingPage";
import HistoryPage from "@/pages/HistoryPage";
import HistoryDetailPage from "@/pages/HistoryDetailPage";
import InventoryPage from "@/pages/InventoryPage";
import BlackFridayPage from "@/pages/BlackFridayPage";
import InventoryDetailPage from "@/pages/InventoryDetailPage";
import AddProductPage from "@/pages/AddProductPage";
import ScanPage from "@/pages/ScanPage";
import ScanCustomerOrderPage from "@/pages/ScanCustomerOrderPage";
import StocktakingPage from "@/pages/StocktakingPage";
import MaintenancePage from "@/pages/MaintenancePage";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import UsersPage from "@/pages/UsersPage";
import StatisticsPage from "@/pages/StatisticsPage";
import OrderCostPage from "@/pages/OrderCostPage";
import InvoiceScanPage from "@/pages/InvoiceScanPage";
import DeliveryPage from "@/pages/DeliveryPage";
import SettingsPage from "@/pages/SettingsPage";
import ImportExportPage from "@/pages/ImportExportPage";
// Purchasing module
import PurchasingPage from "@/pages/purchasing/PurchasingPage";
import SuppliersPage from "@/pages/purchasing/SuppliersPage";
import SupplierFormPage from "@/pages/purchasing/SupplierFormPage";
import PricesPage from "@/pages/purchasing/PricesPage";
import OrdersPage from "@/pages/purchasing/OrdersPage";
import NewOrderPage from "@/pages/purchasing/NewOrderPage";
import ReceiveDeliveryPage from "@/pages/purchasing/ReceiveDeliveryPage";
import ReceivedOrdersPage from "@/pages/purchasing/ReceivedOrdersPage";
import ScanInvoicePage from "@/pages/purchasing/ScanInvoicePage";
import QuickReceivePage from "@/pages/purchasing/QuickReceivePage";
import QuotePage from "@/pages/purchasing/QuotePage";
// Driver/Warehouse flow
import DriverFlowPage from "@/pages/DriverFlowPage";
// AI Scanning
import AIScanPage from "@/pages/AIScanPage";
// Help/Guide
import WorkflowGuidePage from "@/pages/WorkflowGuidePage";

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
}

// Permission-based route wrapper
function PermissionRoute({ children, permissionKey }) {
  const { user, loading, hasPageAccess } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin" replace />;
  }
  
  if (!hasPageAccess(permissionKey)) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
}

// Admin route wrapper
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
}

// Home redirect component - redirects to appropriate page based on permissions
function HomeRedirect() {
  const { user, hasPageAccess } = useAuth();
  
  // Priority order for home page
  if (hasPageAccess("lager")) {
    return <Navigate to="/inventory" replace />;
  }
  if (hasPageAccess("skanna_foljesedel")) {
    return <Navigate to="/ai-scan" replace />;
  }
  if (hasPageAccess("skanna")) {
    return <Navigate to="/scan" replace />;
  }
  if (hasPageAccess("plocklista")) {
    return <Navigate to="/picking" replace />;
  }
  // Fallback - should not happen if permissions are configured correctly
  return <Navigate to="/admin" replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/admin" element={user ? <Navigate to="/home" replace /> : <LoginPage />} />
      
      {/* Protected routes - with permission checks */}
      <Route path="/home" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
      <Route path="/import" element={<AdminRoute><ImportPage /></AdminRoute>} />
      <Route path="/picking" element={<PermissionRoute permissionKey="plocklista"><Dashboard /></PermissionRoute>} />
      <Route path="/picking/:listId" element={<PermissionRoute permissionKey="plocklista"><PickingPage /></PermissionRoute>} />
      <Route path="/history" element={<PermissionRoute permissionKey="administration"><HistoryPage /></PermissionRoute>} />
      <Route path="/history/:listId" element={<PermissionRoute permissionKey="administration"><HistoryDetailPage /></PermissionRoute>} />
      <Route path="/inventory" element={<PermissionRoute permissionKey="lager"><InventoryPage /></PermissionRoute>} />
      <Route path="/black-friday" element={<PermissionRoute permissionKey="lager"><BlackFridayPage /></PermissionRoute>} />
      <Route path="/inventory/new" element={<PermissionRoute permissionKey="lager"><AddProductPage /></PermissionRoute>} />
      <Route path="/inventory/:itemId" element={<PermissionRoute permissionKey="lager"><InventoryDetailPage /></PermissionRoute>} />
      <Route path="/scan" element={<PermissionRoute permissionKey="skanna"><ScanPage /></PermissionRoute>} />
      <Route path="/scan-order" element={<PermissionRoute permissionKey="plocklista"><ScanCustomerOrderPage /></PermissionRoute>} />
      <Route path="/stocktaking" element={<PermissionRoute permissionKey="inventering"><StocktakingPage /></PermissionRoute>} />
      <Route path="/maintenance" element={<PermissionRoute permissionKey="administration"><MaintenancePage /></PermissionRoute>} />
      <Route path="/statistics" element={<PermissionRoute permissionKey="statistik"><StatisticsPage /></PermissionRoute>} />
      <Route path="/order-cost" element={<PermissionRoute permissionKey="statistik"><OrderCostPage /></PermissionRoute>} />
      <Route path="/invoice-scan" element={<PermissionRoute permissionKey="statistik"><InvoiceScanPage /></PermissionRoute>} />
      <Route path="/deliveries" element={<PermissionRoute permissionKey="plocklista"><DeliveryPage /></PermissionRoute>} />
      <Route path="/driver" element={<PermissionRoute permissionKey="lager"><DriverFlowPage /></PermissionRoute>} />
      <Route path="/ai-scan" element={<PermissionRoute permissionKey="skanna_foljesedel"><AIScanPage /></PermissionRoute>} />
      <Route path="/ai-scan/history" element={<Navigate to="/ai-scan?tab=history" replace />} />
      <Route path="/help/workflows" element={<ProtectedRoute><WorkflowGuidePage /></ProtectedRoute>} />
      
      {/* Admin routes */}
      <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
      <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      <Route path="/import-export" element={<AdminRoute><ImportExportPage /></AdminRoute>} />
      
      {/* Purchasing routes */}
      <Route path="/purchasing" element={<PermissionRoute permissionKey="inkop"><PurchasingPage /></PermissionRoute>} />
      <Route path="/purchasing/suppliers" element={<PermissionRoute permissionKey="leverantorer"><SuppliersPage /></PermissionRoute>} />
      <Route path="/purchasing/suppliers/new" element={<PermissionRoute permissionKey="leverantorer"><SupplierFormPage /></PermissionRoute>} />
      <Route path="/purchasing/suppliers/:id" element={<PermissionRoute permissionKey="leverantorer"><SupplierFormPage /></PermissionRoute>} />
      <Route path="/purchasing/suppliers/:supplierId/prices" element={<PermissionRoute permissionKey="prislistor"><PricesPage /></PermissionRoute>} />
      <Route path="/purchasing/prices" element={<PermissionRoute permissionKey="prislistor"><PricesPage /></PermissionRoute>} />
      <Route path="/purchasing/prices/add" element={<PermissionRoute permissionKey="prislistor"><PricesPage /></PermissionRoute>} />
      <Route path="/purchasing/orders" element={<PermissionRoute permissionKey="inkop"><OrdersPage /></PermissionRoute>} />
      <Route path="/purchasing/orders/new" element={<PermissionRoute permissionKey="inkop"><NewOrderPage /></PermissionRoute>} />
      <Route path="/purchasing/orders/:orderId/receive" element={<PermissionRoute permissionKey="inkop"><ReceiveDeliveryPage /></PermissionRoute>} />
      <Route path="/purchasing/orders/received" element={<PermissionRoute permissionKey="inkop"><ReceivedOrdersPage /></PermissionRoute>} />
      <Route path="/purchasing/receive" element={<PermissionRoute permissionKey="inkop"><ReceiveDeliveryPage /></PermissionRoute>} />
      <Route path="/purchasing/scan" element={<ProtectedRoute><ScanInvoicePage /></ProtectedRoute>} />
      <Route path="/purchasing/quick-receive" element={<ProtectedRoute><QuickReceivePage /></ProtectedRoute>} />
      <Route path="/purchasing/quote" element={<PermissionRoute permissionKey="inkop"><QuotePage /></PermissionRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <StocktakingProvider>
            <AppRoutes />
          </StocktakingProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
